import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Associate } from '../models/associate';
import { ViticultureRegistration } from '../models/viticulture-registration';
import { DAP } from '../models/dap';
import { Item } from '../models/item';
import { AssociateGroup } from '../models/associate-group';
import { ItemBrand } from '../models/item-brand';
import { ChemicalClass } from '../models/chemical-class';
import { ActiveIngredient } from '../models/active-ingredient';
import { DetailedItem } from '../models/detailed-item';
import { Classification } from '../models/classification';
import { PriceList } from '../models/price-list';
import { ItemPrice } from '../models/item-price';
import { Package } from '../models/package';
import { Harvest } from '../models/harvest';
import { AssociateAuthorization } from '../models/associate-authorization';

@Injectable({
  providedIn: 'root'
})

export class EntryService {
  constructor(private http: HttpClient) { }

  getAssociates(params?) {
    return this.http.get<Associate[]>(`${environment.API}associates`, { params }).pipe();
  }

  getAssociate(params?) {
    return this.http.get<Associate>(`${environment.API}associates/user`, { params }).pipe();
  }

  getAssignedAssociates(params) {
    const { associateID, ...rest } = params;
    return this.http.get<Associate[]>(`${environment.API}associates/${associateID}/assigned-associates`, { params: rest }).pipe();
  }

  getAssociateAuthorizations(params) {
    const { associateID } = params;
    return this.http.get<AssociateAuthorization[]>(`${environment.API}associates/${associateID}/authorizations`).pipe();
  }

  authorizeAssociate(body) {
    const parentAssociateID = body.get('parentAssociateID');
    return this.http.post<any>(`${environment.API}associates/${parentAssociateID}/authorizations/authorize`, body).pipe();
  }

  revokeAssociateAuthorization(body) {
    const { parentAssociateID, ...rest } = body;
    return this.http.post<any>(`${environment.API}associates/${parentAssociateID}/authorizations/revoke`, rest).pipe();
  }

  updateAssociateProfile(body) {
    const { ID, ...rest } = body;
    return this.http.patch<any>(`${environment.API}associates/${ID}/update-profile`, rest).pipe();
  }

  getViticultureRegistrations(params?) {
    return this.http.get<ViticultureRegistration[]>(`${environment.API}viticultureregistrations`, { params }).pipe();
  }

  getViticultureRegistrationsByGroup(params?) {
    return this.http.get<ViticultureRegistration[]>(`${environment.API}viticultureregistrations`, { params }).pipe();
  }

  getItems(params?) {
    return this.http.get<Item[]>(`${environment.API}items`, { params }).pipe();
  }

  getDetailedItems(params?) {
    return this.http.get<DetailedItem[]>(`${environment.API}items/detailed`, { params }).pipe();
  }

  getItemByID(params) {
    return this.http.get<DetailedItem>(`${environment.API}items/${params.id}`).pipe();
  }

  updateItem(itemID: number, body) {
    return this.http.patch<any>(`${environment.API}items/${itemID}`, body).pipe();
  }

  getItemBrands() {
    return this.http.get<ItemBrand[]>(`${environment.API}items/brands`).pipe();
  }

  getItemPackages(params) {
    return this.http.get<Package[]>(`${environment.API}items/${params.itemID}/packages`).pipe();
  }

  getChemicalClasses() {
    return this.http.get<ChemicalClass[]>(`${environment.API}items/chemical-classes`).pipe();
  }

  getActiveIngredients() {
    return this.http.get<ActiveIngredient[]>(`${environment.API}items/active-ingredients`).pipe();
  }

  getClassifications() {
    return this.http.get<Classification[]>(`${environment.API}items/classifications`).pipe();
  }

  getPriceLists(params?) {
    return this.http.get<PriceList[]>(`${environment.API}priceLists`, { params }).pipe();
  }

  getPriceListByID(params?) {
    return this.http.get<PriceList>(`${environment.API}priceLists/${params.id}`).pipe();
  }

  createPriceList(body) {
    return this.http.post<any>(`${environment.API}priceLists`, body).pipe();
  }

  updatePriceList(priceListID: number, body) {
    return this.http.put<any>(`${environment.API}priceLists/${priceListID}`, body).pipe();
  }

  deletePriceList(params) {
    return this.http.delete<any>(`${environment.API}priceLists/${params.ID}`).pipe();
  }

  getItemPrices(params) {
    const { priceListID } = params;
    return this.http.get<ItemPrice[]>(`${environment.API}priceLists/${priceListID}/item-prices`).pipe();
  }

  createItemPrice(body) {
    const { priceListID, ...rest } = body;
    return this.http.post<any>(`${environment.API}priceLists/${priceListID}/item-prices`, rest).pipe();
  }

  importItemPrices(body) {
    const { priceListID, ...rest } = body;
    return this.http.post<any>(`${environment.API}priceLists/${priceListID}/item-prices/import`, rest).pipe();
  }

  updateItemPrice(itemPriceID: number, body) {
    return this.http.put<any>(`${environment.API}priceLists/0/item-prices/${itemPriceID}`, body).pipe();
  }

  deleteItemPrice(params) {
    return this.http.delete<any>(`${environment.API}priceLists/0/item-prices/${params.ID}`).pipe();
  }

  getDAP(params?) {
    return this.http.get<DAP>(`${environment.API}daps`, { params }).pipe();
  }

  getAssociateGroups(params?) {
    return this.http.get<AssociateGroup[]>(`${environment.API}associategroups`, { params }).pipe();
  }

  getHarvests(params?) {
    return this.http.get<Harvest[]>(`${environment.API}harvests`, { params }).pipe();
  }
}
